import React, { memo, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import './styles/skeleton.css';
import './styles/antd-theme.css';

import { 
    Patient, 
    Request, 
    Messaging, 
    PossessionChangeModal, 
    ErrorBoundary 
} from "./components"
import { useCoupledScroll } from "./hooks";

import { CaseViewProvider, NavigationProvider } from './context';
import { API_TYPES, PHYSICIAN_TAGS } from './constants'
import { createUploadHandlers } from './utils/createUploadHandlers'
import { AudioCallProvider } from './context/AudioCallProvider'
import { USStatesProvider } from './context/USStatesContext'

import { View } from './Styles'

export const CaseView = memo(({
    physicianDetails,
    useTicketByCaseId,
    useCaseDetails,
    apiType = API_TYPES.PHYSICIAN,
    apiClasses,
    readOnlyMode = false,
    allowTagEditing = true,
    canDeleteDocuments = false,
    onNavigateToCase,
    onNavigateToCaseQueue,
    onNavigateBack,
    onTicketClosed,
    caseId,
    requestId,
}) => {
    const id = caseId;
    const requestIdFromQuery = requestId;

    const {
        caseDetails,
        fetchCaseDetails,
        setCaseDetails,
        startConsultation,
        selfAssignCase,
        possessionChangeAction,
        setPossessionChangeAction,
        showLabOrderModal,
        setShowLabOrderModal
    } = useCaseDetails(id, physicianDetails, requestIdFromQuery)

    const uploadHandlers = useMemo(() => {
        return createUploadHandlers(caseDetails, fetchCaseDetails, apiClasses);
    }, [caseDetails, apiClasses, fetchCaseDetails]);


    const { ticket } = useTicketByCaseId(id);
    
    const provider = useMemo(() => 
        caseDetails?.provider || 
        caseDetails?.assignedProvider || 
        caseDetails?.physician,
        [caseDetails]
    );

    const isAssignedProvider = useMemo(() => 
        physicianDetails?.userId?.toString() === caseDetails?.assignedPhysician?.toString(),
        [physicianDetails?.userId, caseDetails?.assignedPhysician]
    );
    
    const isLeadProvider = useMemo(() => 
        physicianDetails?.tags?.includes(PHYSICIAN_TAGS.LEAD_PHYSICIAN),
        [physicianDetails?.tags]
    );
    
    const providerNeedsReview = useMemo(() => 
        physicianDetails?.tags?.includes(PHYSICIAN_TAGS.NEEDS_REVIEW),
        [physicianDetails?.tags]
    );
    
    const isReviewerProvider = useMemo(() => 
        physicianDetails?.tags?.includes(PHYSICIAN_TAGS.REVIEWER),
        [physicianDetails?.tags]
    );

    const computedReadOnlyMode = useMemo(() => {
        if (readOnlyMode === false && apiType === 'physician' && !isAssignedProvider) {
            return true;
        }
        return readOnlyMode;
    }, [readOnlyMode, apiType, isAssignedProvider]);

    const {
        PatientRef,
        RequestRef,
    } = useCoupledScroll();

    const activeRequest = useMemo(() => {
        if (requestIdFromQuery === 'initial') {
            return caseDetails?.requests?.at(-1);
        }
        return caseDetails?.requests?.find(request => request._id === requestIdFromQuery) || caseDetails?.requests?.at(-1);
    }, [requestIdFromQuery, caseDetails?.requests]);
    
    const patient = useMemo(() => ({
        ...caseDetails?.patient,
        heightFt: caseDetails?.heightFt,
        heightIn: caseDetails?.heightIn,
        heightCm: caseDetails && Math.floor((caseDetails?.heightFt * 12 + caseDetails?.heightIn) * 2.54),
        weight: caseDetails?.weight,
        weightKg: Math.floor(caseDetails?.weight * 0.453592)
    }), [caseDetails?.patient, caseDetails?.heightFt, caseDetails?.heightIn, caseDetails?.weight]);

    const history = useHistory();
    const location = useLocation();

    const defaultNavigateToCase = useMemo(() => (caseId, requestId, apiType) => {
        const path = apiType === API_TYPES.ADMIN 
            ? `/cases/${caseId}?requestId=${requestId || 'initial'}`
            : `/dashboard/physician/caseview/${caseId}?requestId=${requestId || 'initial'}`;
        history.push(path);
    }, [history]);

    const defaultNavigateToCaseQueue = useMemo(() => () => {
        const path = '/dashboard/physician/case-queue';
        history.push(path);
    }, [history]);

    const defaultNavigateBack = useMemo(() => () => {
        history.goBack();
    }, [history]);

    const navigateToCase = onNavigateToCase || defaultNavigateToCase;
    const navigateToCaseQueue = onNavigateToCaseQueue || defaultNavigateToCaseQueue;
    const navigateBack = onNavigateBack || defaultNavigateBack;

    const navigationFunctions = useMemo(() => ({
        navigateToCase,
        navigateToCaseQueue,
        navigateBack
    }), [navigateToCase, navigateToCaseQueue, navigateBack]);

    const caseViewContextValue = useMemo(() => {
        const contextValue = {
            ticket,
            caseDetails,
            fetchCaseDetails,
            setCaseDetails,
            activeRequest,
            provider,
            patient,
            startConsultation,
            selfAssignCase,
            showLabOrderModal,
            setShowLabOrderModal,
            apiClasses,
            readOnlyMode: computedReadOnlyMode,
            apiType,
            allowTagEditing,
            isAssignedProvider,
            isLeadProvider,
            providerNeedsReview,
            isReviewerProvider,
            physicianDetails,
            onTicketClosed,
        };
        return contextValue;
    }, [
        ticket,
        caseDetails,
        showLabOrderModal,
        apiClasses,
        computedReadOnlyMode,
        apiType,
        allowTagEditing,
        physicianDetails,
        isAssignedProvider,
        isLeadProvider,
        providerNeedsReview,
        isReviewerProvider,
        onTicketClosed,
    ]);

    if (!RequestRef || !uploadHandlers) return null

    return (
        <ErrorBoundary>
            <NavigationProvider navigationFunctions={navigationFunctions}>
                <CaseViewProvider value={caseViewContextValue}>
                    <USStatesProvider>
                        <AudioCallProvider>
                            <View>
                                <ErrorBoundary>
                                    <Patient patientRef={PatientRef} />
                                </ErrorBoundary>
                                <ErrorBoundary>
                                    <Request requestRef={RequestRef} uploadHandlers={uploadHandlers} canDeleteDocuments={canDeleteDocuments} />
                                </ErrorBoundary>
                                <ErrorBoundary>
                                    <Messaging />
                                </ErrorBoundary>
                            </View>
                            {
                                possessionChangeAction && apiType !== 'admin' && !location.search.includes('one-click-prescribe') && (
                                    <ErrorBoundary>
                                        <PossessionChangeModal
                                            action={possessionChangeAction}
                                            setPossessionChangeAction={setPossessionChangeAction}
                                            startConsultation={startConsultation}
                                            onOk={() => setPossessionChangeAction(null)}
                                        />
                                    </ErrorBoundary>
                                )
                            }
                        </AudioCallProvider>
                    </USStatesProvider>
                </CaseViewProvider>
            </NavigationProvider>
        </ErrorBoundary>
    );
});
